import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  InputAdornment,
  Alert,
  Chip
} from '@mui/material';

export const buildPayload = (formData) => ({
  name: formData.name,
  active: formData.active ? 1 : 0,
  min_price: formData.min_price === '' ? null : parseFloat(formData.min_price),
  max_price: formData.max_price === '' ? null : parseFloat(formData.max_price),
  coefficient: parseFloat(formData.coefficient)
});

export const validateForm = (formData) => {
  if (!formData.name?.trim()) return 'Rule name is required';
  if (formData.coefficient === '' || formData.coefficient == null) return 'Coefficient is required';
  const c = parseFloat(formData.coefficient);
  if (isNaN(c) || c <= 0) return 'Coefficient must be greater than 0';
  if (formData.min_price !== '' && formData.max_price !== '') {
    if (parseFloat(formData.max_price) < parseFloat(formData.min_price)) {
      return 'Max price must be greater than or equal to Min price';
    }
  }
  return null;
};

const CoefficientRuleForm = ({ initialData, onChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    active: true,
    min_price: '',
    max_price: '',
    coefficient: '2.00'
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        active: initialData.active == 1,
        min_price: initialData.min_price ?? '',
        max_price: initialData.max_price ?? '',
        coefficient: initialData.coefficient != null ? String(initialData.coefficient) : '2.00'
      });
    }
  }, [initialData]);

  useEffect(() => { onChange?.(formData); }, [formData]);

  const setField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const coef = parseFloat(formData.coefficient) || 0;
  const markup = coef > 0 ? Math.round((coef - 1) * 100) : 0;

  // Sample base prices for preview
  const samples = [
    { base: 10, label: '€10' },
    { base: 50, label: '€50' },
    { base: 100, label: '€100' },
    { base: 500, label: '€500' }
  ];

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
        Basic Info
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <TextField
            label="Rule Name"
            value={formData.name}
            onChange={(e) => setField('name', e.target.value)}
            fullWidth
            required
            placeholder="e.g. Default × 2, High-value (>€500) × 1.5"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', borderRadius: 1 }}>
            <FormControlLabel
              control={<Switch checked={formData.active} onChange={(e) => setField('active', e.target.checked)} />}
              label={formData.active ? 'Active' : 'Inactive'}
            />
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
        Base Price Range
        <Chip label="Leave both empty for catch-all" size="small" sx={{ ml: 1, fontSize: '10px', height: '18px' }} />
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Matched against the product's <strong>base PRICE</strong> column (before any markup). Leave blank for "no limit".
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Min Price (€)"
            type="number"
            value={formData.min_price}
            onChange={(e) => setField('min_price', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01', min: 0 }}
            placeholder="0"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Max Price (€)"
            type="number"
            value={formData.max_price}
            onChange={(e) => setField('max_price', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01', min: 0 }}
            placeholder="∞"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
        Coefficient (Markup Multiplier)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Sell price = base price × coefficient. Example: 2.00 = +100% markup, 1.5 = +50% markup, 1.0 = no markup.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Coefficient"
            type="number"
            value={formData.coefficient}
            onChange={(e) => setField('coefficient', e.target.value)}
            fullWidth
            required
            inputProps={{ step: '0.01', min: 0.01 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">×</InputAdornment>
            }}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: markup >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
            {markup >= 0 ? `+${markup}%` : `${markup}%`} markup
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 1, border: '1px solid #e5e7eb' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', display: 'block', mb: 1 }}>
              SELL PRICE PREVIEW
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {samples.map(s => (
                <Box key={s.base}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>{s.label} →</Typography>{' '}
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#845adf' }}>
                    €{(s.base * coef).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {(formData.min_price === '' && formData.max_price === '') && (
        <Alert severity="info" sx={{ mt: 2 }}>
          This is a <strong>catch-all rule</strong> — it applies to every product with no rule above it. Place at the bottom of the priority list for fallback behavior.
        </Alert>
      )}
    </Box>
  );
};

export default CoefficientRuleForm;
